import { useTranslations } from 'next-intl';
import { v4 as uuidv4 } from 'uuid';
import { getOfflineAvatarForRecreate } from '@/app/apis';
import { toast } from '@/app/components/ui/use-toast';
import { useStore } from '@/app/providers/store';
import type {
  FormImagePrompt,
  FormTabType,
  ImageRange,
  ModelVersion,
  MultiFrameTransition,
  RecaptionPrompt,
  ReferencePrompt,
  SegmentType,
  SupportedLanguage,
  TaskInfo,
  TaskInputPrompts,
  TaskParams,
  TaskSettings,
  TaskSettingsText2Audio,
  TextPromptWithInputItem,
  ToolFormParams,
  ToolTaskFormVideoPrompt,
  ToolTaskType,
} from '@/app/types';
import {
  appendUnMentionedReferences,
  convertProTextPromptToSimple,
  isOptionAllowed,
  isReferenceAssetSupported,
  type ProTextTranslationFn,
  parseTextToTextPromptWithReference,
  syncImageReferencePrompts,
  validateAndCorrectSettings,
} from '@/app/utils';
import {
  buildOfflineAvatarRecreatePatch,
  buildOneClickMvRecreatePatch,
  buildTTSRecreatePatch,
  isHevcSupported,
  sanitizeToolRecreatePatchReferences,
} from '@/app/utils/client';
import { useLocale } from '@/i18n/routing';
import {
  IMAGE_NAME_PREFIX,
  LATEST_MODEL_VERSION,
  MODEL_VERSION,
  MODEL_VERSION_SETTINGS_MAP,
  normalizeStoryGridSettings,
  SCENE,
  SITE,
} from '../constants';
import type { ViduV1Element } from '../services/vidu';
import type { FormParams, TemplateFormParams } from '../stores/slices/task-form';

type AudioPromptReferenceWithVoiceId = NonNullable<TaskInputPrompts['reference']> & {
  voice_id?: string;
};

/**
 * 将接口返回的输入参数转换为图片上传框的参数（自动处理合并场景）
 *
 * @param imageInputs - 接口返回的输入参数
 * @returns 图片上传框的参数
 */
const convertImagePromptsAPIToForm = (imageInputs: TaskInputPrompts[], isTemplates?: boolean) => {
  //  如果是模版场景，且 imageInputs 只有一张图、 src_imgs 数量不止一张，则是合并的图片，要分别把每张原图回填
  if (imageInputs.length === 1 && isTemplates) {
    const first = imageInputs[0];
    if (Array.isArray(first?.src_imgs) && first.src_imgs.length > 1) {
      const mergeImage = first.content;
      const srcImgs = first.src_imgs;
      return srcImgs.map((p) => ({
        sourceImage: p,
        sourceImagePreview: p,
        selectedImage: p,
        selectedImagePreview: p,
        mergeImage,
      }));
    }
  }

  return imageInputs.map((p) => {
    const sourceImage = p.src_imgs?.[0] ? p.src_imgs[0] : p.content;
    let selectedImageRange: ImageRange | undefined;
    if (p.selected_region) {
      selectedImageRange = [
        p.selected_region.top_left.x,
        p.selected_region.top_left.y,
        p.selected_region.bottom_right.x,
        p.selected_region.bottom_right.y,
      ];
    }

    return {
      sourceImage,
      sourceImagePreview: sourceImage,
      selectedImageRange: selectedImageRange,
      selectedImage: p.content,
      selectedImagePreview: p.content,
    };
  });
};

/**
 * 将接口返回的多帧转场数据转换为表单格式
 *
 * @param inputPrompts - 接口返回的输入参数
 * @param frameDurations - settings 中的帧持续时间数组
 * @param userPlan - 用户订阅计划，用于检查 duration 权限
 * @param modelVersion - 模型版本，用于检查 duration 权限
 * @returns 多帧转场表单数据
 */
const convertMultiFrameAPIToForm = (
  inputPrompts: TaskInputPrompts[],
  frameDurations?: number[],
  userPlan = 'free',
  modelVersion?: ModelVersion,
) => {
  // 直接使用 convertImagePromptsAPIToForm 处理图片
  const rawImagePrompts = inputPrompts.filter((p) => p.type === 'image');
  const imagePrompts = convertImagePromptsAPIToForm(rawImagePrompts).map((p) => ({
    id: uuidv4(),
    status: 'done' as const,
    prompt: p,
  }));

  // 根据 imagePrompts 的数量创建转场（N 张图片 = N-1 个转场）
  const imageCount = imagePrompts.length;
  const transitionCount = imageCount > 1 ? imageCount - 1 : 0;

  // 提取带有 multi_frame.range 的转场文本 prompts，建立索引映射
  const transitionPromptsMap = new Map<number, string>();
  inputPrompts
    .filter((p) => p.type === 'text' && p.multi_frame?.range !== undefined)
    .forEach((p) => {
      const range = p.multi_frame?.range;
      const fromIndex = range?.from;
      if (
        range &&
        fromIndex !== undefined &&
        Number.isInteger(fromIndex) &&
        fromIndex >= 0 &&
        fromIndex < transitionCount
      ) {
        transitionPromptsMap.set(fromIndex, p.content || '');
      }
    });

  // 标记是否有 duration 被调整（权限检查不通过）
  let hasInvalidDuration = false;

  // 创建转场数组，根据 imagePrompts 的数量
  const multiFrameTransitions: MultiFrameTransition[] | undefined =
    transitionCount > 0
      ? Array.from({ length: transitionCount }, (_, index) => {
          const fromIndex = index;
          const toIndex = index + 1;
          const rawDuration = frameDurations?.[fromIndex] ?? 5; // 优先使用 settings.frame_durations，否则默认 5

          // 检查 duration 是否对当前用户套餐可用，如果不可用则使用默认值 5
          const isDurationAllowed = isOptionAllowed('duration', rawDuration.toString(), userPlan, modelVersion);
          const duration = isDurationAllowed ? rawDuration : 5;

          // 如果 duration 被调整，标记为 true
          if (!isDurationAllowed) {
            hasInvalidDuration = true;
          }

          const prompt = transitionPromptsMap.get(fromIndex) || '';

          return {
            duration,
            prompt,
            from: fromIndex,
            to: toIndex,
          } as MultiFrameTransition;
        })
      : undefined;

  return {
    imagePrompts,
    multiFrameTransitions,
    hasInvalidDuration,
  };
};

/**
 * 将接口返回的输入参数转换为 referencePrompts
 *
 * @param inputPrompts - 接口返回的输入参数
 * @param locale - 语言
 * @returns 参考列表
 */
export const convertReferencePromptsAPIToForm = (
  inputPrompts: TaskInputPrompts[],
  locale: SupportedLanguage,
): ReferencePrompt[] => {
  // 根据语言获取图片名称前缀
  const imagePrefix = IMAGE_NAME_PREFIX[SITE][locale];
  let videoIdx = 1;
  let imageIdx = 1;
  let audioIdx = 1;

  const referencePrompts: ReferencePrompt[] = [];

  // 首先得到所有的参考
  inputPrompts
    .filter((p) => p.audit?.audit_violation !== true) // 过滤掉审核失败的数据
    .forEach((p) => {
      if (p.type === 'video') {
        referencePrompts.push({
          prompt: p.content
            ? {
                video: p.content,
                videoPreview: p.content, // 使用 content 作为预览地址
                coverImage: p.cover_uri,
                coverImagePreview: p.cover_uri,
              }
            : null,
          type: 'video' as const,
          status: 'done' as const,
          id: uuidv4(),
          name: p.name || `video${videoIdx++}`, // 优先使用原始name，否则生成新name
        });
      }

      if (p.type === 'image') {
        referencePrompts.push({
          prompt: convertImagePromptsAPIToForm([p])[0],
          type: 'image' as const,
          status: 'done' as const,
          id: uuidv4(),
          name: p.name || `${imagePrefix}${imageIdx++}`, // 优先使用原始name，否则生成新name
        });
      }

      if (p.type === 'audio') {
        const voiceId = (p.reference as AudioPromptReferenceWithVoiceId | undefined)?.voice_id;
        referencePrompts.push({
          prompt: p.content
            ? {
                name: p.name,
                src: p.content,
                preview: p.content,
                originElementVoiceId: voiceId,
                originElementVoiceIsMy: !!voiceId,
                isElementVoiceReference: !!voiceId,
              }
            : null,
          type: 'audio' as const,
          status: 'done' as const,
          id: uuidv4(),
          name: p.name || `audio${audioIdx++}`,
        });
      }

      if (p.type === 'material') {
        referencePrompts.push({
          prompt: p.material
            ? {
                version: p.material.version,
                id: p.material.id,
                name: p.name,
                modality: (p.material as ViduV1Element)?.modality,
                components: p.material.contents?.map((src, index) => ({
                  content: src,
                  type: index === 0 ? 'main' : 'auxiliary',
                  content_type: p.material?.modality === 'video' ? 'video' : 'image', // TODO: modality 为 video 时， component 的 content_type 暂时只能为 video（视频主体暂时不支持传图片）
                  cover_uri: p.material?.modality === 'video' ? p.cover_uri : undefined,
                })),
                ...(p.material?.share_id && {
                  share_meta: {
                    id: p.material?.share_id || '',
                    status: p.material?.share_id !== '0' ? 'approved' : 'unspecified',
                  },
                }),
              }
            : null,
          type: 'element' as const,
          status: 'recreating' as const,
          id: p.material?.id || '',
          name: p.name || '',
        });
      }
    });

  return referencePrompts;
};

/**
 * 将接口返回的输入参数转换为 text2audio 的参数
 *
 * @param inputPrompts - 接口返回的输入参数
 * @returns text2audio 的参数
 */
const convertText2AudioPromptsAPIToForm = (inputPrompts: TaskInputPrompts[]) => {
  return inputPrompts.map(
    (p) =>
      ({
        id: uuidv4(),
        text: p.content,
        startTime: p.timing_text?.from ?? 0,
        endTime: p.timing_text?.to ?? 0,
      }) as SegmentType,
  );
};

const createToolImagePromptFromTaskPrompt = (prompt: TaskInputPrompts): FormImagePrompt | null => {
  if (prompt.type === 'material' && prompt.material?.id) {
    const content = prompt.content || prompt.material.contents?.[0] || '';

    return {
      id: prompt.material.id,
      status: 'done',
      material: {
        id: prompt.material.id,
        version: prompt.material.version || '',
        name: prompt.name,
        share_id: prompt.material.share_id,
      },
      prompt: content
        ? {
            sourceImage: content,
            sourceImagePreview: content,
            selectedImage: content,
            selectedImagePreview: content,
          }
        : null,
    };
  }

  if (prompt.type !== 'image' || !prompt.content) return null;

  const imagePrompt = convertImagePromptsAPIToForm([prompt])[0];
  const referenceCreationId = prompt.reference?.creation_id;

  if (!imagePrompt) return null;

  return {
    id: referenceCreationId || prompt.content,
    status: 'done',
    prompt: imagePrompt,
    reference: referenceCreationId
      ? {
          creation_id: referenceCreationId,
        }
      : undefined,
  };
};

const createFirstToolImagePromptFromTask = (task: Pick<TaskInfo, 'input'>): FormImagePrompt | null => {
  const prompt = task.input?.prompts?.find((item) => item.type === 'image' || item.type === 'material');

  if (!prompt) return null;

  const imagePrompt = createToolImagePromptFromTaskPrompt(prompt);

  if (!imagePrompt) return null;

  return {
    ...imagePrompt,
    id: prompt.reference?.creation_id || task.input?.creation_id || prompt.content,
  };
};

const createToolVideoPromptFromTask = (task: {
  input: TaskInfo['input'];
  settings?: TaskInfo['settings'];
}): ToolTaskFormVideoPrompt | null => {
  const prompt = task.input?.prompts?.find((item) => item.type === 'video');

  if (!prompt?.content) return null;

  const referenceCreationId = prompt.reference?.creation_id || task.input?.creation_id;
  const motionId = prompt.reference?.motion_id;
  const finalReferenceCreationId =
    !!referenceCreationId && referenceCreationId !== '0' ? referenceCreationId : undefined;
  const reference =
    finalReferenceCreationId || motionId
      ? {
          ...(finalReferenceCreationId ? { creation_id: finalReferenceCreationId } : {}),
          ...(motionId ? { motion_id: motionId } : {}),
        }
      : undefined;

  return {
    id: finalReferenceCreationId || motionId || prompt.content,
    status: 'done',
    prompt: {
      video: prompt.content,
      videoPreview: prompt.content,
      duration: typeof task.settings?.duration === 'number' ? task.settings.duration : undefined,
      coverImage: prompt.cover_uri,
      coverImagePreview: prompt.cover_uri,
    },
    reference,
  };
};

const applyToolFormParamsPatch = (
  updateFormParams: (key: keyof ToolFormParams, params: ToolFormParams[typeof key]) => void,
  patch: Partial<ToolFormParams>,
) => {
  if (patch.imagePrompts !== undefined) {
    updateFormParams('imagePrompts', patch.imagePrompts);
  }
  if (patch.videoPrompts !== undefined) {
    updateFormParams('videoPrompts', patch.videoPrompts);
  }
  if (patch.audioPrompts !== undefined) {
    updateFormParams('audioPrompts', patch.audioPrompts);
  }
  if (patch.settings !== undefined) {
    updateFormParams('settings', patch.settings);
  }
  if (patch.creationId !== undefined) {
    updateFormParams('creationId', patch.creationId);
  }
  if (patch.textPrompt !== undefined) {
    updateFormParams('textPrompt', patch.textPrompt);
  }
  if (patch.remixStyle !== undefined) {
    updateFormParams('remixStyle', patch.remixStyle);
  }
  if (patch.audioPrompts !== undefined) {
    updateFormParams('audioPrompts', patch.audioPrompts);
  }
  if (patch.subtitlePrompts !== undefined) {
    updateFormParams('subtitlePrompts', patch.subtitlePrompts);
  }
  if (patch.referenceImagePrompts !== undefined) {
    updateFormParams('referenceImagePrompts', patch.referenceImagePrompts);
  }
  if (patch.oneClickMvVersion !== undefined) {
    updateFormParams('oneClickMvVersion', patch.oneClickMvVersion);
  }
  if (patch.oneClickMvScene !== undefined) {
    updateFormParams('oneClickMvScene', patch.oneClickMvScene);
  }
  if (patch.oneClickMvSubtitleEnabled !== undefined) {
    updateFormParams('oneClickMvSubtitleEnabled', patch.oneClickMvSubtitleEnabled);
  }
  if (patch.oneClickMvSubtitleLanguage !== undefined) {
    updateFormParams('oneClickMvSubtitleLanguage', patch.oneClickMvSubtitleLanguage);
  }
  if ('extraParams' in patch) {
    updateFormParams('extraParams', patch.extraParams);
  }
  if (patch.offlineAvatarTimeline !== undefined) {
    updateFormParams('offlineAvatarTimeline', patch.offlineAvatarTimeline);
  }
  if ('offlineAvatar' in patch) {
    updateFormParams('offlineAvatar', patch.offlineAvatar);
  }
  if (patch.offlineAvatarInputMode !== undefined) {
    updateFormParams('offlineAvatarInputMode', patch.offlineAvatarInputMode);
  }
};

const buildRecreateExtraParams = (inspirationId?: string, source?: string): ToolFormParams['extraParams'] => {
  if (!inspirationId) return undefined;

  return {
    recreate_source: (source as NonNullable<ToolFormParams['extraParams']>['recreate_source']) || 'inspiration',
    recreate_source_id: inspirationId,
  };
};

const buildToolTaskRecreatePatch = async ({
  tool,
  task,
  baseSettings,
}: {
  tool: ToolTaskType;
  task: { id?: string; input: TaskInfo['input']; settings?: TaskInfo['settings'] };
  baseSettings?: TaskInfo['settings'];
}): Promise<Partial<ToolFormParams>> => {
  const firstImagePrompt = createFirstToolImagePromptFromTask(task);
  const imagePrompts =
    task.input?.prompts
      ?.map((prompt) => createToolImagePromptFromTaskPrompt(prompt))
      .filter((prompt): prompt is FormImagePrompt => prompt !== null) || [];
  const videoPrompt = createToolVideoPromptFromTask(task);
  const textPrompt = task.input?.prompts?.find((prompt) => prompt.type === 'text')?.content || '';
  const remixStylePrompt = task.input?.prompts?.find((prompt) => prompt.type === 'remix_style')?.remix_style;
  const remixStyle = remixStylePrompt?.id
    ? {
        id: remixStylePrompt.id,
        name: remixStylePrompt.name,
        url: remixStylePrompt.url,
      }
    : undefined;

  let patch: Partial<ToolFormParams>;

  switch (tool) {
    case 'face_swap':
      patch = {
        ...(firstImagePrompt ? { imagePrompts: [firstImagePrompt] } : {}),
        ...(videoPrompt ? { videoPrompts: [videoPrompt] } : {}),
        ...(task.settings ? { settings: { ...task.settings } } : {}),
      };
      break;
    case 'remake':
      patch = {
        ...(videoPrompt ? { videoPrompts: [videoPrompt] } : {}),
        imagePrompts,
        textPrompt,
        ...(task.settings ? { settings: { ...(baseSettings || {}), ...task.settings } } : {}),
      };
      break;
    case 'one_click':
      patch = {
        imagePrompts,
        textPrompt,
        ...(task.settings ? { settings: { ...(baseSettings || {}), ...task.settings } } : {}),
      };
      break;
    case 'music_video':
      patch = buildOneClickMvRecreatePatch({ task, baseSettings });
      break;
    case 'style_transfer':
      patch = {
        imagePrompts: firstImagePrompt ? [firstImagePrompt] : [],
        textPrompt,
        ...(remixStyle ? { remixStyle } : {}),
        ...(task.settings ? { settings: { ...(baseSettings || {}), ...task.settings } } : {}),
      };
      break;
    case 'motion_control':
      patch = {
        imagePrompts: firstImagePrompt ? [firstImagePrompt] : [],
        videoPrompts: videoPrompt ? [videoPrompt] : [],
        ...(task.settings ? { settings: { ...(baseSettings || {}), ...task.settings } } : {}),
      };
      break;
    default:
      patch = {};
  }

  return sanitizeToolRecreatePatchReferences(patch);
};

const splitOrEmpty = (str: string | undefined | null): string[] | undefined => {
  return str && str.length > 0 && str !== 'None' ? str.split(', ') : undefined;
};

/**
 * 将接口返回的专业模式数据转换为表单数据格式
 *
 * @param prompt - 接口返回的专业模式参数
 * @returns 专业模式表单数据
 */
const convertProModeAPIToForm = (content: string): RecaptionPrompt | undefined => {
  try {
    const parsedContent = JSON.parse(content);
    const { Subject, Environment, Style, ...rest } = parsedContent;

    return {
      subject: parseTextToTextPromptWithReference(Subject),
      environment: parseTextToTextPromptWithReference(Environment),
      style: splitOrEmpty(Style) ?? [],
      shotSize: splitOrEmpty(rest['Shot Size']),
      focalLength: splitOrEmpty(rest['Focal Length']),
      composition: splitOrEmpty(rest.Composition),
      cameraAngle: splitOrEmpty(rest['Camera Angle']),
      specialFilmingTechnique: splitOrEmpty(rest['Special Filming Technique']),
      cameraMovement: splitOrEmpty(rest['Camera Movement']),
      motionLevel: splitOrEmpty(rest['Motion Level']) ?? [],
    };
  } catch (error) {
    console.error('Failed to parse professional mode data:', error);
    return undefined;
  }
};

/**
 * 将接口返回的 model_version 反向转换为表单使用的 modelVersion
 */
const convertModelVersionFromAPI = (settings?: TaskInfo['settings']): ModelVersion => {
  const modelVersion = settings?.model_version;
  if (!modelVersion) {
    return LATEST_MODEL_VERSION;
  }

  // 1.0 和 1.5 模型已下线，做同款强制切换到最新模型
  if (modelVersion === '1.0' || modelVersion === '1.5') {
    return LATEST_MODEL_VERSION;
  }

  return modelVersion as ModelVersion;
};

const convertSettingsAPIToForm = (
  settings?: TaskInfo['settings'],
  formTabType?: Exclude<FormTabType, 'scene'>,
  handleShowToast?: () => void,
) => {
  if (!settings || formTabType === undefined) return {};

  // 忽略 1.0 模型的 model 字段
  const { model: _model, codec, ...rest } = settings;

  const defaultCodec = isHevcSupported() ? 'h265' : 'h264';

  const newSettings: TaskSettings = {
    sample_count: 1, // 兼容旧版：没有 sample_count 时，设置为 1
    schedule_mode: 'normal' as TaskInfo['settings']['schedule_mode'], // 兼容旧版：没有 schedule_mode 时，设置为 normal
    codec: codec && codec !== 'unspecified' ? codec : defaultCodec, // 如果生成时没有指定或为 unspecified，默认使用支持的编码格式
    ...rest,
  };

  // 确保关键字段存在，即使是 undefined，让后续的 validateAndCorrectSettings 能进入修正分支
  // 修复：接口不返回 duration 时，该字段会完全缺失，导致校验层的 `in` 守卫跳过，最终 store 里存入 undefined
  if (!('duration' in newSettings)) {
    newSettings.duration = undefined;
  }

  if (!isHevcSupported() && newSettings?.codec?.startsWith('h265')) {
    // 即使是因为兼容性检查而修改，如果原本是会员选项，也需要有 toast 提示
    if (!isOptionAllowed('codec', newSettings.codec, 'free')) {
      handleShowToast?.();
    }
    newSettings.codec = 'h264';
  }

  // 使用辅助函数进行反向转换
  const modelVersion = convertModelVersionFromAPI(settings);

  // Q3 图生、文生、参考生如果没有 transition 字段，做同款增加默认值 pro 模式
  if (
    modelVersion === MODEL_VERSION.Q3 &&
    (formTabType === 'img2video' || formTabType === 'text2video' || formTabType === 'reference2image') &&
    (!newSettings.transition || newSettings.transition === 'unspecified')
  ) {
    newSettings.transition = 'pro';
  }

  return {
    [MODEL_VERSION_SETTINGS_MAP[modelVersion]]: validateAndCorrectSettings({
      version: modelVersion,
      newSettings,
      type: formTabType,
    }),
  };
};

/**
 * 根据模式状态处理文本内容的通用逻辑
 */
const processTextContent = <T extends 'text' | 'with-reference'>(
  textContent: string,
  originalIsProMode: boolean,
  canUseProMode: boolean,
  t: ProTextTranslationFn,
  outputFormat: T,
  defaultProcessor: (text: string) => T extends 'text' ? string : TextPromptWithInputItem,
): T extends 'text' ? string : TextPromptWithInputItem => {
  // 如果原本是专业模式但用户无法使用专业模式，需要转换专业模式内容为简单模式
  if (originalIsProMode && !canUseProMode && textContent) {
    try {
      const proModeData = convertProModeAPIToForm(textContent);
      if (proModeData) {
        return convertProTextPromptToSimple(proModeData, {
          t,
          outputFormat,
        }) as T extends 'text' ? string : TextPromptWithInputItem;
      }
    } catch {
      // 转换失败，使用回退处理器
    }
  }

  return defaultProcessor(textContent);
};

/**
 * 处理【做同款】数据
 * @returns
 */
export const useProcessRecreateData = () => {
  const {
    setFormParams,
    updateModelVersion,
    updateText2AudioFormParams,
    updateText2TtsFormParams,
    updateFaceSwapFormParams,
    updateRemakeFormParams,
    updateOneClickVideoProductionFormParams,
    updateOneClickMvFormParams,
    updateStyleTransferFormParams,
    updateMotionControlFormParams,
    updateOfflineAvatarFormParams,
    oneClickVideoProductionSettings,
    oneClickMvSettings,
    remakeSettings,
    userInfo,
    trialsProMode,
  } = useStore((state) => ({
    userInfo: state.userInfo,
    setFormParams: state.setFormParams,
    updateModelVersion: state.updateModelVersion,
    updateText2AudioFormParams: state.updateText2AudioFormParams,
    updateText2TtsFormParams: state.updateText2TtsFormParams,
    updateFaceSwapFormParams: state.updateFaceSwapFormParams,
    updateRemakeFormParams: state.updateRemakeFormParams,
    updateOneClickVideoProductionFormParams: state.updateOneClickVideoProductionFormParams,
    updateOneClickMvFormParams: state.updateOneClickMvFormParams,
    updateStyleTransferFormParams: state.updateStyleTransferFormParams,
    updateMotionControlFormParams: state.updateMotionControlFormParams,
    updateOfflineAvatarFormParams: state.updateOfflineAvatarFormParams,
    oneClickVideoProductionSettings: state.oneClickVideoProductionFormParams.settings,
    oneClickMvSettings: state.oneClickMvFormParams.settings,
    remakeSettings: state.remakeFormParams.settings,
    trialsProMode: state.trialsProMode,
  }));
  const locale = useLocale();
  const t = useTranslations();
  const tPro = useTranslations('create.proMode');

  // 处理【做同款】场景数据
  const processRecreateData = ({
    tab,
    settings,
    input,
    templateScene,
    creationId,
    inspirationId,
    source,
    taskType,
  }: {
    tab: TaskParams['type'];
    settings?: TaskInfo['settings'];
    input: TaskInfo['input'];
    templateScene?: string;
    creationId: string;
    inspirationId?: string;
    source?: string;
    taskType: TaskInfo['type'];
  }) => {
    const rawImagePrompts = input.prompts?.filter((p) => p.type === 'image') || [];

    // 用于特殊处理主体化数据
    const isReferencePromptForm =
      settings?.model_version && isReferenceAssetSupported(settings.model_version as ModelVersion, tab);

    let formData: Partial<FormParams | TemplateFormParams> | undefined;
    let version: ModelVersion | undefined;

    const originalIsProMode = input.editor_mode === 'professional';

    // 判断免费用户是否可以使用专业模式
    const isFreeUser = userInfo?.subs_plan === 'free';
    const canUseProMode = !isFreeUser || !!trialsProMode?.remaining;

    // 如果是免费用户且没有专业模式试用次数，强制关闭专业模式
    const isProMode = originalIsProMode && canUseProMode;

    // 处理模版
    if (tab === SCENE) {
      const imagePrompts = convertImagePromptsAPIToForm(rawImagePrompts, true);

      const formData: TemplateFormParams = {
        textPrompt: input?.prompts?.find((p) => p.type === 'text')?.content || '',
        imagePrompts: imagePrompts.map((p, idx) => ({
          id: uuidv4(),
          status: 'done',
          prompt: p,
          presetDemoId: rawImagePrompts[idx]?.template?.preset_demo_id,
        })),
        templateScene,
        settings: {
          has_audio: input.prompts?.some((p) => p.type === 'audio'),
          sample_count: settings?.sample_count,
          aspect_ratio: settings?.aspect_ratio,
        },
        inspirationId: inspirationId || creationId,
        inspirationFrom: source,
      };

      setFormParams(SCENE, formData);
      return;
    }

    // 处理文生音频
    if (tab === 'text2audio') {
      updateText2AudioFormParams({
        segments: convertText2AudioPromptsAPIToForm(input.prompts || []),
        settings: settings as TaskSettingsText2Audio,
      });
      return;
    }
    if (tab === 'tts') {
      applyToolFormParamsPatch(updateText2TtsFormParams, {
        ...buildTTSRecreatePatch({ input, settings }),
        extraParams: buildRecreateExtraParams(inspirationId, source),
      });
      return;
    }

    // 处理分镜（storyGrid）做同款
    if (tab === 'short_story') {
      const referencePrompts = input.prompts ? convertReferencePromptsAPIToForm(input.prompts, locale) : [];

      const textContent = input.prompts?.find((p) => p.type === 'text')?.content || '';
      const textPromptWithReference = parseTextToTextPromptWithReference(textContent, referencePrompts);
      const finalTextPromptWithReference = appendUnMentionedReferences(textPromptWithReference, referencePrompts);

      formData = {
        referencePrompts,
        textPromptWithReference: finalTextPromptWithReference,
        isProMode: false,
        storyGridSettings: normalizeStoryGridSettings({
          grid_count: settings?.grid_count,
          duration: settings?.duration,
          resolution: settings?.resolution,
          aspect_ratio: settings?.aspect_ratio,
        }),
        inspirationId: inspirationId,
        inspirationFrom: source,
      };

      setFormParams('storyGrid', formData);
      return;
    }

    const handleShowToast = () => {
      if (!isFreeUser) return;
      toast({
        type: 'info',
        message: t('popup.toast.invalidSetting'),
      });
    };

    if (isReferencePromptForm && input.prompts) {
      const referencePrompts = convertReferencePromptsAPIToForm(input.prompts, locale);

      // 处理文本类型
      const textContent = input.prompts.find((p) => p.type === 'text')?.content || '';

      const textPromptWithReference = processTextContent(
        textContent,
        originalIsProMode,
        canUseProMode,
        tPro,
        'with-reference' as const,
        (text: string) => parseTextToTextPromptWithReference(text, referencePrompts),
      );

      // 兼容旧参考生任务的做同款（主体资产化上线前的图片没有被 @）
      const finalTextPromptWithReference = appendUnMentionedReferences(textPromptWithReference, referencePrompts);

      // 使用辅助函数进行反向转换
      version = convertModelVersionFromAPI(settings);

      formData = {
        referencePrompts,
        textPromptWithReference: finalTextPromptWithReference,
        recaptionPrompt: isProMode && textContent ? convertProModeAPIToForm(textContent) : undefined,
        isProMode,
        ...convertSettingsAPIToForm(settings, tab as Exclude<FormTabType, 'scene'>, handleShowToast),
        inspirationId: inspirationId,
        inspirationFrom: source,
      };
    } else {
      let imagePrompts: Array<{
        id: string;
        status: 'done';
        prompt: ReturnType<typeof convertImagePromptsAPIToForm>[0];
      }>;
      let multiFrameTransitions: MultiFrameTransition[] | undefined;

      // 根据 taskType 判断是否使用多帧转场转换逻辑
      if (taskType === 'multi_frame' && input.prompts) {
        // 使用辅助函数进行反向转换
        const modelVersion = convertModelVersionFromAPI(settings);
        // 使用多帧转场专用转换函数，传入 settings.frame_durations、userPlan 和 modelVersion 来还原转场持续时间
        const multiFrameData = convertMultiFrameAPIToForm(
          input.prompts,
          settings?.frame_durations,
          userInfo?.subs_plan || 'free',
          modelVersion,
        );
        imagePrompts = multiFrameData.imagePrompts;
        multiFrameTransitions = multiFrameData.multiFrameTransitions;

        // 如果有 duration 被调整（权限检查不通过），显示 toast
        if (multiFrameData.hasInvalidDuration) {
          toast({
            type: 'info',
            message: t('popup.toast.invalidSetting'),
          });
        }
      } else {
        // 使用普通图片转换
        imagePrompts = convertImagePromptsAPIToForm(rawImagePrompts).map((p) => ({
          id: uuidv4(),
          status: 'done' as const,
          prompt: p,
        }));
      }

      // 只处理不带 multi_frame.range 的文本
      const textPrompt = input?.prompts?.find((p) => p.type === 'text' && !p.multi_frame?.range)?.content;

      let finalTextPrompt = '';

      if (textPrompt) {
        finalTextPrompt = processTextContent(
          textPrompt,
          originalIsProMode,
          canUseProMode,
          tPro,
          'text' as const,
          (text: string) => text,
        );
      } else if (!isProMode) {
        finalTextPrompt = '';
      }

      // 使用辅助函数进行反向转换
      version = convertModelVersionFromAPI(settings);

      formData = {
        textPrompt: finalTextPrompt,
        recaptionPrompt: isProMode && textPrompt ? convertProModeAPIToForm(textPrompt) : undefined,
        imagePrompts,
        ...(tab === 'img2video' && { multiFrameTransitions }),
        isProMode,
        ...convertSettingsAPIToForm(settings, tab as Exclude<FormTabType, 'scene'>, handleShowToast),
        inspirationId: inspirationId,
        inspirationFrom: source,
      };
    }

    // 1.0 和 1.5 模型已下线，做同款强制切换到最新模型（并同步图片/文本到目标版本的数据结构）
    if (settings?.model_version === '1.0' || settings?.model_version === '1.5') {
      const nextVersion = LATEST_MODEL_VERSION;

      // 参考生需要把图片同步到主体资产化的格式
      if (tab === 'character2video') {
        const updates = syncImageReferencePrompts({
          prevVersion: settings?.model_version,
          nextVersion,
          formParams: formData || {},
          locale,
        });

        // 将同步结果写回 formData，再统一 set
        if (updates.referencePrompts) {
          formData.referencePrompts = updates.referencePrompts;
        }
        if (updates.textPromptWithReference) {
          formData.textPromptWithReference = updates.textPromptWithReference;
        }
      }

      version = nextVersion;
      toast({
        message: t('popup.toast.modelOffline', { modelVersion: settings?.model_version }),
      });
    }

    version && updateModelVersion(tab as Exclude<FormTabType, 'scene'>, version);
    formData && setFormParams(tab as Exclude<FormTabType, 'video2extail'>, formData);
  };

  const processToolRecreateData = async ({
    taskId,
    taskType,
    settings,
    input,
  }: {
    taskId?: string;
    taskType: TaskInfo['type'];
    settings?: TaskInfo['settings'];
    input: TaskInfo['input'];
  }) => {
    const task = { id: taskId, input, settings };

    switch (taskType) {
      case 'face_swap':
        applyToolFormParamsPatch(updateFaceSwapFormParams, await buildToolTaskRecreatePatch({ tool: taskType, task }));
        return;
      case 'remake':
        applyToolFormParamsPatch(
          updateRemakeFormParams,
          await buildToolTaskRecreatePatch({ tool: taskType, task, baseSettings: remakeSettings }),
        );
        return;
      case 'one_click':
        applyToolFormParamsPatch(
          updateOneClickVideoProductionFormParams,
          await buildToolTaskRecreatePatch({ tool: taskType, task, baseSettings: oneClickVideoProductionSettings }),
        );
        return;
      case 'music_video':
        applyToolFormParamsPatch(
          updateOneClickMvFormParams,
          await buildToolTaskRecreatePatch({ tool: taskType, task, baseSettings: oneClickMvSettings }),
        );
        return;
      case 'style_transfer':
        applyToolFormParamsPatch(
          updateStyleTransferFormParams,
          await buildToolTaskRecreatePatch({ tool: taskType, task }),
        );
        return;
      case 'motion_control':
        applyToolFormParamsPatch(
          updateMotionControlFormParams,
          await buildToolTaskRecreatePatch({ tool: taskType, task }),
        );
        return;
      case 'tts':
        applyToolFormParamsPatch(updateText2TtsFormParams, buildTTSRecreatePatch(task));
        return;
      case 'offline_avatar':
        {
          const avatarId = input.prompts?.find(
            (prompt) => prompt.type === 'offline_avatar' && prompt.reference?.offline_avatar_id,
          )?.reference?.offline_avatar_id;
          let offlineAvatar: Awaited<ReturnType<typeof getOfflineAvatarForRecreate>>;

          if (avatarId) {
            offlineAvatar = await getOfflineAvatarForRecreate(avatarId);
            if (!offlineAvatar) {
              toast({
                type: 'info',
                message: t('tools.offlineAvatar.avatar.recreateDeleted'),
              });
            }
          }

          const patch = buildOfflineAvatarRecreatePatch(task);
          applyToolFormParamsPatch(updateOfflineAvatarFormParams, {
            ...patch,
            ...(offlineAvatar ? { offlineAvatar } : avatarId ? { offlineAvatar: undefined } : {}),
          });
        }
        return;
      default:
        return;
    }
  };

  return {
    processRecreateData,
    processToolRecreateData,
  };
};
